import { FormControlLabel, Stack, Typography, useTheme } from "@mui/material";
import { Radio, RadioButtonGroup } from "components";
import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { CompoundTaxTab, SingleTaxTab } from "./components";
import toaster from "react-hot-toast";
import { useFormContext } from "react-hook-form";
import { useCreateTaxConfig, useUpdateTaxConfig } from "controller";

interface AddTaxContentProps {
  onSubmit: (data?: any) => void;
  mode?: {
    method: "create" | "update";
    data: any;
    primaryButtonDisable: boolean;
  };
  closeDrawer?: () => void;
}

export const AddTaxContent = forwardRef(
  ({ onSubmit, mode, closeDrawer }: AddTaxContentProps, ref) => {
    const theme = useTheme();
    const [selectedTaxType, setSelectedTaxType] = useState<string>("single");
    const { mutateAsync: createTaxConfig, isLoading: isCreateTaxLoading } =
      useCreateTaxConfig();
    const { mutateAsync: updateTaxConfig, isLoading: isUpdateTaxLoading } =
      useUpdateTaxConfig();

    const taxRef = useRef<any>(null);

    const taxTypeData = [
      {
        label: "Single tax",
        value: "single",
      },
      {
        label: "Combined tax",
        value: "combined",
      },
    ];

    const onSaveHandler = async (data: any) => {
      try {
        if (mode?.method === "update") {
          await updateTaxConfig({
            ...data,
            tax_id: mode?.data?.mapping_id,
          });
          onSubmit(data);
          return;
        }
        await createTaxConfig(data);
        onSubmit(data);
      } catch (e: any) {
        toaster.error(e?.message || "Unable to save at the moment");
      }
    };

    const onSubmitHandler = async () => {
      try {
        const data = await taxRef?.current?.onSubmit?.();
        if (!data) {
          return;
        }
        const taxData = {
          ...data,
          is_percentage: true,
          is_custom: false,
          is_compound_tax: selectedTaxType === "combined",
        };
        onSaveHandler(taxData);
      } catch (e) {
        toaster.error("Unable to save at the moment");
      }
    };

    useImperativeHandle(ref, () => ({
      onSubmit: onSubmitHandler,
      isLoading: () => {
        return isCreateTaxLoading || isUpdateTaxLoading;
      },
      cancelButtonClick: () => {
        if (!closeDrawer) return;
        closeDrawer();
        // reset();
      },
    }));

    const valueCheck = () => {
      if (mode?.method === "update") {
        return mode?.data?.is_compound_tax ? "combined" : "single";
      }
      return selectedTaxType;
    };

    return (
      <Stack sx={{ height: "100%", position: "relative" }}>
        <Stack
          gap="16px"
          sx={{
            height: "100%",
            overflow: "auto",
            padding: "16px 16px 100px 16px",
          }}
        >
          <Typography variant="body-medium">
            Select the type of tax you wish to add
          </Typography>
          <RadioButtonGroup
            data={taxTypeData}
            labelMappingKey="label"
            disabled={mode?.method === "update"}
            valueMappingKey="value"
            // value={selectedTaxType}
            value={valueCheck()}
            onChange={(value) => setSelectedTaxType(value)}
            row={true}
            RadioGroupProps={{
              gap: "16px",
              display: "flex",
              width: "100%",
            }}
            customRadioButton={(isChecked, item, { disabled, onChange }) => (
              <FormControlLabel
                sx={{
                  ...theme.typography["body-medium"],
                  color: isChecked
                    ? theme.palette.primary.main
                    : theme.palette.lightBg.main,
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                  margin: "0px",
                  border: `1px solid ${
                    isChecked
                      ? theme.palette.primary.main
                      : theme.palette.neutral["020"]
                  }`,
                  borderRadius: "60px",
                  p: "0px 0px 0px 16px",
                  transition: "all 0.3s ease",
                  backgroundColor: isChecked
                    ? theme.palette.primary.tint
                    : theme.palette.darkBg.main,
                }}
                value={item["value"]}
                control={
                  <Radio {...item?.radioButtonProps} checked={isChecked} />
                }
                label={item["label"] as React.ReactNode}
                labelPlacement="start"
              />
            )}
          />
          {valueCheck() === "single" && (
            <SingleTaxTab ref={taxRef} mode={mode} />
          )}
          {valueCheck() === "combined" && (
            <CompoundTaxTab ref={taxRef} mode={mode} />
          )}
        </Stack>
      </Stack>
    );
  },
);
